import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    User, Phone, ArrowRight, ArrowLeft,
    Loader2, LogOut,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    selectUser,
    logoutUser,
    logout,
    updateRegistrationStatus,
} from '../store/slices/authSlice';
import {
    updateUserDetails,
    selectUserDetails,
    selectIsSubmitting,
    selectRegistrationError,
    clearRegistrationError,
    completeRegistration,
} from '../store/slices/registrationSlice';

const UserDetailsForm = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const user = useAppSelector(selectUser);
    const userDetails = useAppSelector(selectUserDetails);
    const isSubmitting = useAppSelector(selectIsSubmitting);
    const error = useAppSelector(selectRegistrationError);

    const [localErrors, setLocalErrors] = React.useState({});

    useEffect(() => {
        return () => { dispatch(clearRegistrationError()); };
    }, [dispatch]);

    const handleLogout = () => {
        dispatch(logoutUser());
        dispatch(logout());
    };

    const handleChange = (field) => (e) => {
        const value = e.target.value;

        // Validation for name fields - no numbers allowed
        if ((field === 'firstName' || field === 'lastName') && /\d/.test(value)) {
            return;
        }

        // Phone number - only digits, spaces, +, -, ()
        if (field === 'phone' && value && !/^[\d\s+\-()]*$/.test(value)) {
            return;
        }

        dispatch(updateUserDetails({ [field]: value }));
        setLocalErrors(prev => ({ ...prev, [field]: null }));
        if (error) dispatch(clearRegistrationError());
    };

    const validate = () => {
        const errors = {};

        if (!userDetails.firstName?.trim()) errors.firstName = 'First name is required';
        if (!userDetails.lastName?.trim()) errors.lastName = 'Last name is required';
        if (!userDetails.phone?.trim()) errors.phone = 'Business mobile number is required';

        setLocalErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        const result = await dispatch(completeRegistration(userDetails));
        if (completeRegistration.fulfilled.match(result)) {
            dispatch(updateRegistrationStatus(true));
            navigate('/setup-organisation', { replace: true });
        }
    };

    const inputBaseClass = "w-full py-3 bg-white border rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none text-gray-900 placeholder-gray-400";
    const inputWithIconClass = `${inputBaseClass} pl-11 pr-4`;
    const inputNoIconClass = `${inputBaseClass} pl-4 pr-4`;

    const getInputBorderClass = (field) =>
        localErrors[field] ? 'border-red-300' : 'border-gray-200';

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Top bar */}
            <div className="bg-white border-b border-gray-100">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-emerald-600 rounded-xl flex items-center justify-center">
                            <span className="text-white font-display font-bold text-base">W</span>
                        </div>
                        <span className="text-gray-900 font-display font-bold text-xl tracking-tight">wadakaroo</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <span className="text-sm text-gray-500 hidden sm:block">{user?.email}</span>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 bg-transparent border-none cursor-pointer"
                        >
                            <LogOut size={16} />
                            <span className="hidden sm:inline">Sign out</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-6 py-10">
                {/* Step indicator */}
                <div className="flex items-center gap-2 mb-8">
                    <div className="flex items-center gap-1.5">
                        <div className="w-8 h-1.5 bg-emerald-500 rounded-full" />
                        <div className="w-8 h-1.5 bg-emerald-500 rounded-full" />
                        <div className="w-8 h-1.5 bg-gray-200 rounded-full" />
                    </div>
                    <span className="text-xs text-gray-400 ml-2">Step 2 of 3</span>
                </div>

                <div className="mb-8">
                    <h2 className="text-2xl font-display font-bold text-gray-900 mb-2">
                        Complete your profile
                    </h2>
                    <p className="text-gray-500">
                        Tell us about yourself so we can personalise your experience
                    </p>
                </div>

                <form onSubmit={handleSubmit}>
                    {(error) && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                            <p className="text-red-700 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Owner Details Card */}
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
                        <div className="flex items-center gap-2 mb-5">
                            <User size={18} className="text-emerald-600" />
                            <h3 className="font-semibold text-gray-900">Owner details</h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    First name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={userDetails.firstName}
                                    onChange={handleChange('firstName')}
                                    className={`${inputNoIconClass} ${getInputBorderClass('firstName')}`}
                                    placeholder="John"
                                    required
                                />
                                {localErrors.firstName && (
                                    <p className="text-red-500 text-xs mt-1">{localErrors.firstName}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Last name <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={userDetails.lastName}
                                    onChange={handleChange('lastName')}
                                    className={`${inputNoIconClass} ${getInputBorderClass('lastName')}`}
                                    placeholder="Smith"
                                    required
                                />
                                {localErrors.lastName && (
                                    <p className="text-red-500 text-xs mt-1">{localErrors.lastName}</p>
                                )}
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                    Business mobile number <span className="text-red-400">*</span>
                                </label>
                                <div className="relative">
                                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                    <input
                                        type="tel"
                                        value={userDetails.phone}
                                        onChange={handleChange('phone')}
                                        className={`${inputWithIconClass} ${getInputBorderClass('phone')}`}
                                        placeholder="04XX XXX XXX"
                                        required
                                    />
                                </div>
                                {localErrors.phone && (
                                    <p className="text-red-500 text-xs mt-1">{localErrors.phone}</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                        <button
                            type="button"
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-gray-600 hover:text-gray-800 bg-transparent border-none cursor-pointer"
                        >
                            <ArrowLeft size={16} />
                            Back
                        </button>

                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold text-sm transition-all ${
                                isSubmitting
                                    ? 'bg-emerald-300 cursor-not-allowed'
                                    : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]'
                            } text-white shadow-sm`}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="animate-spin" size={18} />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    Continue
                                    <ArrowRight size={16} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserDetailsForm;